import {Component, Inject, OnInit} from '@angular/core'
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef} from '@angular/material/dialog'
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors} from '@angular/forms'
import {CommonModule} from '@angular/common'
import {AutoCompleteModule} from 'primeng/autocomplete'
import {MatSelectModule} from '@angular/material/select'
import {MatFormFieldModule} from '@angular/material/form-field'
import {MatOptionModule} from '@angular/material/core'
import {IUser} from '../../../shared/model/user.model'
import {BuyerService} from '../buyer-list/buyer.service'
import {SpinnerOverlayService} from '../../../Components/spinner/spinner-overlay.service'
import {CustomerService} from '../../../shared/service/customer-service';
import {ICustomer, ICustomerResponseList} from '../../../shared/model/customer.model';
import {WarehouseService} from '../../../shared/service/warehouse-service';
import {IWarehouse} from '../../../shared/model/warehouse.model';

@Component({
  selector: 'app-comprador-update',
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule
  ],
  templateUrl: './buyer-update.html',
  styleUrl: './buyer-update.css'
})
export class BuyerUpdate implements OnInit {
  form!: FormGroup
  filteredCustomers: ICustomer[] = []
  selectedCustomer: ICustomer | null = null
  filteredWarehouses: IWarehouse[] = []
  selectedWarehouses: IWarehouse[] = []
  buyer: IUser | null = null
  selectedRoles: string[] = []

  constructor(private spinner: SpinnerOverlayService, private fb: FormBuilder, public dialogRef: MatDialogRef<BuyerUpdate>, @Inject(MAT_DIALOG_DATA) public data: any, private compradorService: BuyerService, private customerService: CustomerService, private warehouseService: WarehouseService) {
  }

  ngOnInit() {
    this.form = this.fb.group({
      name: [this.buyer?.user?.name ?? '', [Validators.required]],
      email: [this.buyer?.user?.email ?? '', [Validators.required, Validators.email]],
      addressNumber: [{value: this.buyer?.user?.addressNumber ?? '', disabled: true}, [Validators.required]],
      warehouses: [[]],
      phone: [this.buyer?.user?.phone ?? '', [Validators.required, Validators.maxLength(14), Validators.minLength(10), Validators.pattern(/^\+?\d+$/)]],
      active: this.buyer?.user?.active ?? true
    })

    // Inicializar roles por defecto
    this.selectedRoles = ['COMPRADOR']
    this.updateWarehouseValidation()

    if (this.data) {
      this.findById()
    }
  }

  findById() {
    this.compradorService.findById(this.data.id).subscribe({
      next: (response) => {
        this.buyer = response.body ?? null
        const buyerData: any = this.buyer;
        
        this.form.patchValue({
          name: this.buyer?.user?.name,
          email: this.buyer?.user?.email,
          addressNumber: this.buyer?.user?.addressNumber,
          phone: this.buyer?.user?.phone,
          active: this.buyer?.user?.active
        });
        
        // Cargar roles del usuario
        const userRoles = this.buyer?.user?.roles?.map(r => r.code ?? '') ?? ['COMPRADOR']
        this.selectedRoles = userRoles.filter(role => role)
        this.updateWarehouseValidation()
        
        // Cargar cliente y bodegas
        const addressNumber = this.buyer?.user?.addressNumber
        const warehouseCodes = (buyerData as any)?.warehouseCodes || (buyerData as any)?.user?.warehouseCodes || []
        
        if (addressNumber) {
          this.loadCustomerAndWarehouse(addressNumber, warehouseCodes)
        } else {
          this.handleLoading(false)
        }
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  loadCustomerAndWarehouse(addressNumber: number, warehouseCodes: string[] = []) {
    this.customerService.getCustomerDataByANs([addressNumber]).subscribe({
      next: (response) => {
        const customers = (response.body as any)?.customers ?? []
        if (customers.length > 0) {
          const customer = customers[0]
          this.selectedCustomer = {
            an: customer.an,
            name: customer.name
          } as ICustomer
          
          this.loadWarehouses(addressNumber, warehouseCodes)
        } else {
          this.handleLoading(false)
        }
      },
      error: (err) => {
        console.error('Error al cargar cliente:', err)
        this.handleLoading(false)
      }
    })
  }

  searchCustomer(event: any) {
    const query = event.query
    if (!query || query.trim() === '') {
      this.filteredCustomers = []
      return
    }

    this.customerService.findByName(query)
      .subscribe({
        next: (response) => {
          this.filteredCustomers = response.body?.customer ?? []
        },
        error: () => {
          this.filteredCustomers = []
        }
      })
  }

  close(save: boolean): void {
    this.dialogRef.close(save)
  }

  get f() {
    return this.form.controls
  }

  handleSelection(event: any) {
    this.selectedCustomer = event.value

    this.form.setValue({
      name: '',
      email: '',
      addressNumber: this.selectedCustomer?.an,
      warehouses: [],
      phone: '',
      active: true
    })

    // Reiniciar roles al valor por defecto
    this.selectedRoles = ['COMPRADOR']
    this.updateWarehouseValidation()

    // Cargar bodegas del cliente seleccionado
    if (this.selectedCustomer?.an) {
      this.loadWarehouses(this.selectedCustomer.an)
    }
  }

  loadWarehouses(customerAn: number, warehouseCodesToSelect: string[] = []) {
    this.warehouseService.findByCustomerAn(customerAn).subscribe({
      next: (response) => {
        this.filteredWarehouses = response.body?.warehouses ?? []
        
        if (warehouseCodesToSelect.length > 0 && this.filteredWarehouses.length > 0) {
          this.selectedWarehouses = this.filteredWarehouses.filter(
            w => warehouseCodesToSelect.includes(w.code ?? '')
          )
          this.form.patchValue({ warehouses: this.selectedWarehouses })
        }
        
        this.handleLoading(false)
      },
      error: () => {
        this.filteredWarehouses = []
        this.handleLoading(false)
      }
    })
  }

  handleWarehouseSelection(event: any) {
    this.selectedWarehouses = event.value ?? []
  }

  onRoleSelectionChange(selection: string) {
    if (selection === 'AMBOS') {
      this.selectedRoles = ['COMPRADOR', 'DUEÑO']
    } else {
      this.selectedRoles = [selection]
    }
    
    // Actualizar validación y limpiar bodega si es solo DUEÑO
    this.updateWarehouseValidation()
    
    if (selection === 'DUEÑO') {
      this.form.patchValue({ warehouses: [] })
      this.selectedWarehouses = []
    }
  }

  updateWarehouseValidation() {
    const warehouseControl = this.form.get('warehouses')
    if (this.isRoleSelected('COMPRADOR')) {
      warehouseControl?.setValidators([this.nonEmptyArrayValidator.bind(this)])
    } else {
      warehouseControl?.clearValidators()
    }
    warehouseControl?.updateValueAndValidity()
  }

  nonEmptyArrayValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !Array.isArray(control.value) || control.value.length === 0) {
      return { required: true }
    }
    return null
  }

  getRoleSelection(): string {
    if (this.selectedRoles.length === 2) {
      return 'AMBOS'
    } else if (this.selectedRoles.length === 1) {
      return this.selectedRoles[0]
    }
    return 'COMPRADOR' // valor por defecto
  }

  isRoleSelected(role: string): boolean {
    return this.selectedRoles.includes(role)
  }

  save() {
    // Validar que al menos un rol esté seleccionado
    if (this.selectedRoles.length === 0) {
      this.form.markAllAsTouched()
      return
    }

    this.handleLoading(true)
    const formValue = this.form.getRawValue()

    const payload: any = {
      id: this.buyer?.user.id,
      active: formValue.active,
      email: formValue.email,
      name: formValue.name,
      roles: this.selectedRoles.map(role => ({ code: role })),
      addressNumber: formValue.addressNumber,
      phone: formValue.phone
    }

    if (this.isRoleSelected('COMPRADOR') && formValue.warehouses?.length > 0) {
      payload.warehouseCodes = formValue.warehouses.map((w: IWarehouse) => w.code)
    }

    if (!this.buyer?.user.id) {

      this.compradorService.save(payload).subscribe({
        next: (response) => {
          this.handleLoading(true)
          this.close(true)
        },
        error: () => {
          this.handleLoading(false)
        }
      })

    } else {
      this.compradorService.update(payload).subscribe({
        next: (response) => {
          this.handleLoading(true)
          this.close(true)
        },
        error: () => {
          this.handleLoading(false)
        }
      })
    }
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }
}
